import React, { useState } from 'react';
import { ErrorMessage } from '../ErrorMessage';
import { LuAsterisk } from 'react-icons/lu';

type InputFieldProps = {
  id: string;
  className?: string;
  placeholder?: string;
  label?: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  required?: boolean;
  icon?: React.ComponentType;
  max?: number;
  min?: number;
  step?: number;
  error?: string;
  maxLength?: number;
  disabled?: boolean;
  darkMode?: boolean; // Add darkMode prop
};

const TextArea: React.FC<InputFieldProps> = ({
  id,
  className = '',
  placeholder,
  label,
  value,
  onChange,
  required = false,
  icon: Icon,
  max,
  min,
  step,
  error,
  maxLength,
  disabled,
  darkMode = false, // Default to false
}) => {
  const [isFocused, setIsFocused] = useState(true);

  return (
    <div className={`relative ${className}`}>
      {label && isFocused && (
        <label
          htmlFor={id}
          className={`flex text-xs font-[600] pl-[15px] mb-1 transition-colors ${
            darkMode ? 'text-gray-300' : 'text-gray-700'
          }`}
        >
          {required && (
            <span className="text-red-600 top-[2px] relative">
              <LuAsterisk />
            </span>
          )}
          {label}
        </label>
      )}

      <div className='relative'>
        {Icon && (
          <span className={`absolute p-[5px] rounded-sm top-[8px] left-[7px] text-xl transition-colors ${
            darkMode ? 'text-gray-400' : 'text-[#98A1B0]'
          }`}>
            <Icon />
          </span>
        )}

        <textarea
          id={id}
          className={`focus:outline-none focus:shadow-outline border rounded-[3px] pl-10 px-4 py-3 w-full text-md transition-colors ${
            darkMode 
              ? 'bg-neutral-700 border-neutral-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500' 
              : 'border-gray-400 text-gray-700 focus:shadow-lg focus:border-[#58595c]'
          } ${error && 'border-red-500 dark:border-red-500 focus:ring-red-500'} ${
            disabled && (darkMode ? 'bg-neutral-600 text-gray-400 cursor-not-allowed' : 'bg-gray-100 text-gray-500 cursor-not-allowed')
          } ${className}`}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onChange}
          onFocus={() => setIsFocused(true)}
          autoComplete="off"
          maxLength={maxLength}
          disabled={disabled}
          rows={4}
        ></textarea>

        {error && <ErrorMessage message={error} />}
      </div>
    </div>
  );
};

export default TextArea;